import type { Store } from 'jotai/vanilla/store'
import type { Position } from '@/voxelMath'
import type { EventTuple } from '@/events/types'

export type SubjectRef = { kind: string; id: string }

export type Quaternion = { x: number; y: number; z: number; w: number }

export type Transform = {
  translate: Position
  rotation: Quaternion
}

export interface Subject {
  readonly kind: string
  readonly id: string
  center(): Position
  bbox(): { min: Position; max: Position }
  getTransform(): Transform
  setTransform(transform: Transform): void
  clampTransform?(transform: Transform, bounds: Position): Transform
  beginInteraction(): void
  endInteraction(): void
  previewEvents(): EventTuple[]
  commitTransform(): EventTuple[]
  // Optional: incremental rotation hook used by rotation gizmo.
  // Implement when the subject supports rotation. Delta is in radians about axis.
  onRotate?(axis: 'x' | 'y' | 'z', deltaRad: number): Subject
}

type SubjectProvider = (id: string, store: Store) => Subject | null

const registry = new Map<string, SubjectProvider>()

export function registerSubjectProvider(kind: string, provider: SubjectProvider): void {
  registry.set(kind, provider)
}

export function getSubject(ref: SubjectRef | null, store: Store): Subject | null {
  if (!ref) return null
  const provider = registry.get(ref.kind)
  if (!provider) return null
  return provider(ref.id, store)
}

export function subjectTransformKey(ref: SubjectRef): string {
  return `${ref.kind}:${ref.id}`
}
